import type {Dispatch, PropsWithChildren} from 'react'
import {createContext, useCallback, useContext, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useFirestore} from '@goschool/react-firebase'
import {doc, getDoc, setDoc} from 'firebase/firestore'
import type {UserCredential} from 'firebase/auth'
import {AuthErrorCodes} from 'firebase/auth'
import {FirebaseError} from 'firebase/app'
import type {AuthProviderId} from './AuthenticationContext'
import {useAuthenticationContext} from './AuthenticationContext'
import {errorMessage} from './util'
import * as Sentry from '@sentry/react'

export type SignInState = 'idle' | 'pending' | 'success' | 'failed'

export interface SignInContextType {
  state: SignInState
  setState: Dispatch<SignInState>

  error: string | null

  handleError: (e: unknown, provider: AuthProviderId) => void
  handleSuccess: (credential: UserCredential) => Promise<void>
  resetError: () => void
}


export const SignInContext = createContext<SignInContextType | undefined>(undefined)


interface SignInContextProviderProps extends PropsWithChildren {
  mode: 'signIn' | 'signUp'

}

export function SignInContextProvider({children, mode}: SignInContextProviderProps) {
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<SignInState>('idle')
  const resetError = useCallback(() => setError(null), [])
  const {hideAuthentication} = useAuthenticationContext()
  const {i18n} = useTranslation()
  const firestore = useFirestore()

  const setLanguage = useCallback(
    async (credential: UserCredential) => {
      try {
        const navigatorLang = typeof navigator !== 'undefined' && (navigator.language || navigator.languages && navigator.languages[0])
        const rawLang = i18n?.language ?? navigatorLang
        if (rawLang && credential?.user?.uid) {
          const langCode = rawLang.split('-')[0]
          const userLanguage = langCode === 'hu' ? 'hu' : 'en'
          const profileRef = doc(firestore, 'users', credential.user.uid)
          const profileSnapshot = await getDoc(profileRef)
          if (!profileSnapshot.data()?.language) {
            await setDoc(profileRef, {language: userLanguage}, {merge: true})
          }
        }
      } catch (writeErr) {
        try {
          Sentry.captureException(writeErr)
        } catch (_) { /* ignore */
        }
      }
    }, [firestore, i18n]
  )

  const handleSuccess = useCallback(
    async (credential: UserCredential) => {
      setState('success')
      setError(null)
      hideAuthentication()
      setState('idle')
      await setLanguage(credential)
    }, [hideAuthentication, setLanguage]
  )

  const handleError = useCallback(
    (e: unknown, provider: AuthProviderId) => {
      setState('failed')
      // Expected auth errors (user entering bad credentials) should not create Sentry
      // error events. Only record unexpected errors to Sentry to avoid noise.
      const isExpectedAuthError = e instanceof FirebaseError && (
        e.code === 'auth/invalid-credential' ||
        e.code === 'auth/invalid-email' ||
        e.code === 'auth/wrong-password' ||
        e.code === 'auth/missing-password' ||
        e.code === AuthErrorCodes.INVALID_LOGIN_CREDENTIALS
      )

      if (!isExpectedAuthError) {
        try {
          Sentry.captureException(e, {data: {mode, provider}})
        } catch (_) { /* ignore Sentry failures */ }
      } else {
        // keep a breadcrumb for diagnostics
        Sentry.addBreadcrumb({category: 'auth', message: 'Expected auth failure', level: 'info', data: {mode, provider, error: (e as FirebaseError).code}})
      }

      if (e instanceof FirebaseError && (e.code === 'auth/invalid-credential' || e.code === AuthErrorCodes.INVALID_LOGIN_CREDENTIALS)) {
        setError('Invalid email or password')
      } else if (e instanceof FirebaseError && e.code === 'auth/invalid-email') {
        setError('Invalid email')
      } else if (e instanceof FirebaseError && e.code === 'auth/missing-password') {
        setError('Missing password')
      } else {
        setError(errorMessage(e))
      }
    },
    [mode]
  )

  return <SignInContext.Provider value={{state, setState, error, handleError, handleSuccess, resetError}}>
    {children}
  </SignInContext.Provider>
}


export function useSignInContext() {
  const context = useContext(SignInContext)
  if (context === undefined) {
    throw new Error('useSignInContext must be used within a SignInContextProvider')
  }
  return context

}
